import React from "react";
import { useLang } from "../../context/LanguageContext";
import { Star, Quote } from "lucide-react";
import { Reveal } from "./Reveal";

export const Reviews = () => {
  const { t } = useLang();

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-24 md:py-40 bg-white border-y border-stone-100"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: rating big */}
          <div className="lg:col-span-5">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.35em] text-amber-800/80">
                — {t.reviews.overline}
              </span>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="mt-6 font-heading text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-stone-900">
                {t.reviews.title}
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <div className="mt-12 flex items-baseline gap-4">
                <span className="font-heading text-8xl md:text-9xl text-stone-900 leading-none">
                  8.6
                </span>
                <span className="font-heading text-4xl text-stone-400">/10</span>
              </div>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-4 flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i < 4
                        ? "fill-amber-500 text-amber-500"
                        : "fill-amber-200 text-amber-200"
                    }`}
                  />
                ))}
              </div>
              <div className="mt-5 text-sm text-stone-600 font-light">
                {t.reviews.rating}
                <br />
                <span className="text-stone-400">{t.reviews.count}</span>
              </div>
            </Reveal>

            <Reveal delay={280}>
              <div className="mt-10 grid grid-cols-3 gap-4 border-t border-stone-200 pt-8">
                {[
                  ["9.0", "Comfort"],
                  ["8.9", "Cleanliness"],
                  ["8.8", "Staff"],
                ].map(([n, l]) => (
                  <div key={l}>
                    <div className="font-heading text-2xl text-stone-900">{n}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-1">
                      {l}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Right: testimonials */}
          <div className="lg:col-span-7 space-y-8">
            {t.reviews.items.map((r, i) => (
              <Reveal key={i} delay={120 * i}>
                <article
                  data-testid={`review-${i}`}
                  className={`relative p-8 md:p-10 border border-stone-100 bg-[#FDFBF7] ${
                    i === 1 ? "md:ml-8 lg:ml-12" : ""
                  }`}
                >
                  <Quote className="absolute top-6 right-6 w-6 h-6 text-amber-800/40" />
                  <p className="font-heading text-2xl md:text-3xl italic leading-snug text-stone-800 max-w-xl">
                    "{r.text}"
                  </p>
                  <div className="mt-6 flex items-center gap-4 text-sm">
                    <div className="w-10 h-10 rounded-full bg-amber-800/10 flex items-center justify-center font-heading text-lg text-amber-800">
                      {r.author[0]}
                    </div>
                    <div>
                      <div className="text-stone-900 font-medium">{r.author}</div>
                      <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mt-0.5">
                        {r.country}
                      </div>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
